/**
 * What: Presents the compact product thumbnail used in a Shopify study index row.
 * Transport / data-flow: Receives product data already loaded on StudySetup; performs no fetching or state changes.
 *
 */
import type { ProductSummary } from '../types'
import { Box, Thumbnail } from '@shopify/polaris'
import { ImageIcon } from '@shopify/polaris-icons'

type StudySetupCardSummaryProps = {
  product: Pick<ProductSummary, 'title'> & Partial<Pick<ProductSummary, 'imageUrl' | 'imageAlt'>>
}

function getThumbnailSource(product: StudySetupCardSummaryProps['product']) {
  return product.imageUrl?.trim() || undefined
}

function getThumbnailAlt(product: StudySetupCardSummaryProps['product']) {
  if (!product.imageUrl?.trim()) return `No image available for ${product.title}`
  return product.imageAlt?.trim() || `Photo of ${product.title}`
}

/**
 * Keeps the product visual compact and independent from product and study names. A product with no image (for
 * example, a test from connection history) shows Polaris's image icon, not a broken image.
 */
export function StudySetupCardSummary({ product }: StudySetupCardSummaryProps) {
  return (
    <Box data-testid="study-product-thumbnail-frame" width="40px" minWidth="40px">
      <Thumbnail source={getThumbnailSource(product) ?? ImageIcon} alt={getThumbnailAlt(product)} size="small" />
    </Box>
  )
}
